package fr.frux.feature.site.detail

import androidx.compose.foundation.layout.Column
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Flag
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.SnackbarMessageEffect
import fr.frux.core.designsystem.error.userMessage
import fr.frux.feature.site.resources.Res
import fr.frux.feature.site.resources.site_delete
import fr.frux.feature.site.resources.site_delete_cancel
import fr.frux.feature.site.resources.site_delete_confirm
import fr.frux.feature.site.resources.site_delete_text
import fr.frux.feature.site.resources.site_delete_title
import fr.frux.feature.site.resources.site_owner_claimed
import fr.frux.feature.site.resources.site_owner_rule_changed
import fr.frux.feature.site.resources.site_owner_unclaim_confirm
import fr.frux.feature.site.resources.site_owner_unclaim_text
import fr.frux.feature.site.resources.site_owner_unclaim_title
import fr.frux.feature.site.resources.site_owner_unclaimed
import fr.frux.feature.site.resources.site_planting_added
import fr.frux.feature.site.resources.site_report
import fr.frux.feature.site.resources.site_report_sent
import fr.frux.feature.site.resources.site_reported
import fr.frux.shared.domain.model.Site
import org.jetbrains.compose.resources.stringResource

/** Feuilles et dialogue ouverts par les gestes de la fiche, portés par l'état du ViewModel. */
@Composable
internal fun SiteDetailOverlays(
    state: SiteDetailState,
    viewModel: SiteDetailViewModel,
) {
    val site = state.site ?: return
    if (state.reportOpen) {
        SiteReportSheet(
            hasOwner = site.hasOwner,
            draft = state.reportDraft,
            onDraft = viewModel::onReportDraft,
            onSubmit = viewModel::submitReport,
            onDismiss = viewModel::dismissSheets,
        )
    }
    if (state.plantingOpen) {
        AddPlantingSheet(
            query = state.speciesQuery,
            results = state.speciesResults,
            choice = state.plantingChoice,
            onQuery = viewModel::onSpeciesQuery,
            onChoice = viewModel::onPlantingChoice,
            onSubmit = viewModel::addPlanting,
            onDismiss = viewModel::dismissSheets,
        )
    }
    if (state.deletePrompt) {
        AlertDialog(
            onDismissRequest = viewModel::dismissSheets,
            title = { Text(stringResource(Res.string.site_delete_title)) },
            text = { Text(stringResource(Res.string.site_delete_text)) },
            confirmButton = {
                TextButton(onClick = viewModel::confirmDelete) { Text(stringResource(Res.string.site_delete_confirm)) }
            },
            dismissButton = {
                TextButton(onClick = viewModel::dismissSheets) { Text(stringResource(Res.string.site_delete_cancel)) }
            },
        )
    }
    val ownership by viewModel.ownership.state.collectAsStateWithLifecycle()
    if (ownership.unclaimPrompt) {
        AlertDialog(
            onDismissRequest = viewModel.ownership::cancelUnclaim,
            title = { Text(stringResource(Res.string.site_owner_unclaim_title)) },
            text = { Text(stringResource(Res.string.site_owner_unclaim_text)) },
            confirmButton = {
                TextButton(onClick = { viewModel.ownership.confirmUnclaim(site.id) }) {
                    Text(stringResource(Res.string.site_owner_unclaim_confirm))
                }
            },
            dismissButton = {
                TextButton(onClick = viewModel.ownership::cancelUnclaim) {
                    Text(stringResource(Res.string.site_delete_cancel))
                }
            },
        )
    }
}

@Composable
internal fun SiteDetailMessageEffect(
    message: SiteDetailMessage?,
    snackbarHostState: SnackbarHostState,
    onShown: () -> Unit,
) {
    val text =
        when (message) {
            SiteDetailMessage.Reported -> stringResource(Res.string.site_report_sent)
            SiteDetailMessage.PlantingAdded -> stringResource(Res.string.site_planting_added)
            SiteDetailMessage.Claimed -> stringResource(Res.string.site_owner_claimed)
            SiteDetailMessage.AccessRuleChanged -> stringResource(Res.string.site_owner_rule_changed)
            SiteDetailMessage.Unclaimed -> stringResource(Res.string.site_owner_unclaimed)
            is SiteDetailMessage.Failed -> message.error.userMessage()
            null -> null
        }
    SnackbarMessageEffect(trigger = message, message = text, snackbarHostState = snackbarHostState) { onShown() }
}

/**
 * « Signaler ce lieu », absent sur son propre lieu et inactif une fois signalé, puis « Supprimer
 * ce lieu » pour qui en a le droit.
 */
@Composable
internal fun SiteModerationActions(
    site: Site,
    isMine: Boolean,
    onReport: () -> Unit,
    onDelete: () -> Unit,
) {
    Column {
        if (!isMine) {
            FruxButton(
                text = stringResource(if (site.reportedByMe) Res.string.site_reported else Res.string.site_report),
                onClick = onReport,
                enabled = !site.reportedByMe,
                style = FruxButtonStyle.Text,
                icon = Icons.Outlined.Flag,
            )
        }
        if (site.canDelete) {
            FruxButton(
                text = stringResource(Res.string.site_delete),
                onClick = onDelete,
                style = FruxButtonStyle.Text,
                icon = Icons.Outlined.Delete,
            )
        }
    }
}
